import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Button, Card, Col, Input, InputNumber, Row, Select } from 'antd';
import PageTitle from '@/components/PageTitle';
import BaseRadioList from '@/components/Elements/BaseRadios';
import BaseSelect from '@/components/Elements/BaseSelect';
import { _newLine } from '@/utils/helpers';
import { rewardGetConfig, rewardUpdateConfig } from '@/redux/actions/rewardSpinActions';
import { getListProvinces, getListWards } from '@/redux/actions/regionsActions';
import { fetchActiveCustomers } from '@/redux/actions/customerActions';
import { getActiveCustomerProspects } from '@/redux/actions/customerProspectsActions';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/reward-config', breadcrumbName: 'Cấu hình quay thưởng' }
];

class RewardSpinConfig extends Component {
    state = {
        isLoading: false,
        wards: [],
        customerQuery: '',
        customerType: 'potential',
        showButton: 'no'
    };

    componentDidMount() {
        this.loadConfig();
        this.props.getListProvinces();
    }

    loadConfig = () => {
        this.setState({ isLoading: true });
        return this.props.rewardGetConfig()
            .then(config => this.applyConfig(config))
            .finally(() => this.setState({ isLoading: false }));
    };

    applyConfig = config => {
        if (!config) return Promise.resolve();
        const customerType = config.customer_type || 'potential';
        const showButton = config.show_button || 'no';
        this.setState({ customerType, showButton });
        this.loadCustomers(customerType, { page: 1 });

        const loadWards = config.province_code
            ? this.props.getListWards({ province_code: config.province_code })
                .then(response => this.setState({ wards: response.data || [] }))
            : Promise.resolve(this.setState({ wards: [] }));

        return loadWards.then(() => this.props.form.setFieldsValue({
            customer_type: customerType,
            province_code: config.province_code || undefined,
            wards_code: config.wards_code || [],
            date_to_expire: Number(config.date_to_expire || 0),
            ignore_customer: config.ignore_customer || 'no',
            show_button: showButton,
            allow_ips: config.allow_ips || '',
            excluded_customer: (config.excluded_customer || []).map(Number),
            note: _newLine(config.note || '')
        }));
    };

    loadCustomers = (customerType, filter = {}) => customerType === 'potential'
        ? this.props.getActiveCustomerProspects(filter)
        : this.props.fetchActiveCustomers(filter);

    submit = event => {
        event.preventDefault();
        this.props.form.validateFields((error, values) => {
            if (error) return;
            this.setState({ isLoading: true });
            this.props.rewardUpdateConfig(values)
                .then(() => this.loadConfig())
                .finally(() => this.setState({ isLoading: false }));
        });
    };

    changeProvince = provinceCode => {
        this.props.form.setFieldsValue({ wards_code: [] });
        if (!provinceCode) {
            this.setState({ wards: [] });
            return;
        }
        this.props.getListWards({ province_code: provinceCode })
            .then(response => this.setState({ wards: response.data || [] }));
    };

    changeCustomerType = customerType => {
        this.props.form.setFieldsValue({ excluded_customer: [] });
        this.setState({ customerType, customerQuery: '' });
        this.loadCustomers(customerType, { page: 1 });
    };

    searchCustomers = keyword => {
        this.setState({ customerQuery: keyword });
        this.loadCustomers(this.state.customerType, { keyword, page: 1 });
    };

    loadMoreCustomers = () => {
        const { customerType, customerQuery } = this.state;
        const pagination = customerType === 'potential' ? this.props.prospectPagination : this.props.customerPagination;
        if (!pagination || pagination.currentPage * pagination.perPage >= pagination.total) return;
        this.loadCustomers(customerType, { keyword: customerQuery, page: pagination.currentPage + 1 });
    };

    renderCustomerOptions = customers => (customers || []).map(customer => (
        <Select.Option key={customer.id} value={customer.id} label={customer.full_name}>
            {customer.full_name} ({customer.code})
        </Select.Option>
    ));

    render() {
        const { getFieldDecorator } = this.props.form;
        const { config, provinces, activeCustomers, activeProspects } = this.props;
        const { isLoading, wards, customerType, showButton } = this.state;
        const loadedCustomerOptions = customerType === 'potential' ? activeProspects : activeCustomers;
        const customerOptions = [...(config?.excluded_customer_options || []), ...(loadedCustomerOptions || [])]
            .filter((customer, index, list) => list.findIndex(item => item.id === customer.id) === index);

        return (
            <div>
                <PageTitle routes={routes} title="Cấu hình quay thưởng" />
                <Card size="small" className="mt-3">
                    <Form layout="vertical" onSubmit={this.submit}>
                        <Row gutter={16}>
                            <Col xs={24} md={24}>
                                <Form.Item label="Loại khách hàng">
                                    {getFieldDecorator('customer_type', {
                                        initialValue: config?.customer_type || 'potential',
                                        rules: [{ required: true, message: 'Vui lòng chọn loại khách hàng.' }]
                                    })(
                                        <BaseRadioList
                                            disabled
                                            options={[
                                                { label: 'Khách hàng tiềm năng', value: 'potential' },
                                                { label: 'Khách hàng', value: 'customer' }
                                            ]}
                                            onChange={this.changeCustomerType}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Tỉnh/Thành phố">
                                    {getFieldDecorator('province_code', { initialValue: config?.province_code || undefined })(
                                        <BaseSelect
                                            options={provinces}
                                            optionValue="code"
                                            optionLabel="name"
                                            showSearch
                                            placeholder="Tỉnh/Thành phố"
                                            className="w-100"
                                            onChange={this.changeProvince}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={16}>
                                <Form.Item label="Phường/Xã">
                                    {getFieldDecorator('wards_code', { initialValue: config?.wards_code || [] })(
                                        <Select
                                            mode="multiple"
                                            className="w-100"
                                            placeholder="Chọn một hoặc nhiều Phường/Xã"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                        >
                                            {wards.map(item => <Select.Option key={item.code} value={item.code}>{item.name}</Select.Option>)}
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Hạn nhận giải thưởng (ngày)">
                                    {getFieldDecorator('date_to_expire', {
                                        initialValue: Number(config?.date_to_expire || 0),
                                        rules: [{ required: true, message: 'Vui lòng nhập hạn nhận giải.' }]
                                    })(<InputNumber min={0} max={3650} precision={0} style={{ width: '100%' }} />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Bỏ qua khách hàng đã quay">
                                    {getFieldDecorator('ignore_customer', {
                                        initialValue: config?.ignore_customer || 'no',
                                        rules: [{ required: true, message: 'Vui lòng chọn tùy chọn.' }]
                                    })(<BaseRadioList options={[{ label: 'Có', value: 'yes' }, { label: 'Không', value: 'no' }]} />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item label="Hiển thị nút quay">
                                    {getFieldDecorator('show_button', {
                                        initialValue: config?.show_button || 'no',
                                        rules: [{ required: true, message: 'Vui lòng chọn tùy chọn.' }]
                                    })(
                                        <BaseRadioList
                                            options={[{ label: 'Có', value: 'yes' }, { label: 'Không', value: 'no' }]}
                                            onChange={value => this.setState({ showButton: value })}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            {showButton === 'yes' && (
                                <Col xs={24}>
                                    <Form.Item label="Danh sách IP được phép hiển thị nút quay">
                                        {getFieldDecorator('allow_ips', {
                                            initialValue: config?.allow_ips || '',
                                            rules: [{ required: true, message: 'Vui lòng nhập danh sách IP.' }]
                                        })(<Input.TextArea rows={3} placeholder="Mỗi địa chỉ IP cách nhau bằng dấu phẩy (,)" />)}
                                    </Form.Item>
                                </Col>
                            )}
                            <Col xs={24}>
                                <Form.Item label="Loại trừ khách hàng">
                                    {getFieldDecorator('excluded_customer', { initialValue: config?.excluded_customer || [] })(
                                        <Select
                                            mode="multiple"
                                            className="w-100"
                                            placeholder="Nhập tên/mã và chọn khách hàng cần loại trừ"
                                            allowClear
                                            showSearch
                                            filterOption={false}
                                            onSearch={this.searchCustomers}
                                            onPopupScroll={this.loadMoreCustomers}
                                        >
                                            {this.renderCustomerOptions(customerOptions)}
                                        </Select>
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24}>
                                <Form.Item label="Ghi chú">
                                    {getFieldDecorator('note', { initialValue: _newLine(config?.note || '') })(<Input.TextArea rows={3} />)}
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button htmlType="submit" type="primary" loading={isLoading}>Cập nhật</Button>
                    </Form>
                </Card>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    config: state.rewardSpinReducer.config,
    provinces: state.regionsReducer.list_provinces,
    activeCustomers: state.customerReducer.activeList.items,
    customerPagination: state.customerReducer.activeList.pagination,
    activeProspects: state.customerProspectsReducer.active.items,
    prospectPagination: state.customerProspectsReducer.active.pagination
});

const mapDispatchToProps = dispatch => ({
    rewardUpdateConfig: data => dispatch(rewardUpdateConfig(data)),
    rewardGetConfig: () => dispatch(rewardGetConfig()),
    getListProvinces: () => dispatch(getListProvinces()),
    getListWards: filter => dispatch(getListWards(filter)),
    fetchActiveCustomers: filter => dispatch(fetchActiveCustomers(filter)),
    getActiveCustomerProspects: filter => dispatch(getActiveCustomerProspects(filter))
});

export default connect(mapStateToProps, mapDispatchToProps)(Form.create({ name: 'RewardSpinConfig' })(RewardSpinConfig));
